import { useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface Source {
  type: 'document' | 'web';
  title: string;
  url?: string;
  document_id?: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: Source[];
  token_count?: number;
  created_at: string;
}

interface MessageBubbleProps {
  message: Message;
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const isUser = message.role === 'user';
  const hasSources = message.sources && message.sources.length > 0;

  return (
    <div
      className={cn(
        'flex w-full mb-4',
        isUser ? 'justify-end' : 'justify-start',
      )}
    >
      <div className={cn('max-w-[75%] space-y-1', isUser ? 'items-end' : 'items-start')}>
        {/* Role label */}
        <div
          className={cn(
            'text-xs font-medium text-muted-foreground px-1',
            isUser ? 'text-right' : 'text-left',
          )}
        >
          {isUser ? 'You' : 'AI'}
          <span className="ml-2 text-[10px] text-muted-foreground/70">
            {formatTime(message.created_at)}
          </span>
        </div>

        {/* Message content */}
        {isUser ? (
          <div className="rounded-2xl rounded-br-md bg-primary px-4 py-2.5 text-primary-foreground">
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          </div>
        ) : (
          <Card className="rounded-2xl rounded-bl-md">
            <CardContent className="p-4">
              <div className="prose prose-sm max-w-none text-sm">
                <p className="whitespace-pre-wrap m-0">{message.content}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sources */}
        {!isUser && hasSources && (
          <div className="flex flex-wrap gap-1.5 px-1 pt-1">
            {message.sources!.map((source, idx) =>
              source.type === 'document' ? (
                <Badge key={idx} variant="secondary" className="text-[10px] font-normal">
                  {source.title}
                </Badge>
              ) : (
                <a
                  key={idx}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline"
                >
                  {source.title}
                  <ExternalLink className="h-2.5 w-2.5" />
                </a>
              ),
            )}
          </div>
        )}

        {/* Token metadata (collapsible) */}
        {!isUser && message.token_count != null && (
          <div className="px-1">
            <button
              onClick={() => setDetailsOpen(!detailsOpen)}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
            >
              {detailsOpen ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
              Details
            </button>
            {detailsOpen && (
              <div className="mt-1 rounded border bg-muted/50 px-2 py-1 text-[10px] text-muted-foreground">
                <span>Tokens: {message.token_count}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
